import axios from 'axios';
import Cookies from 'js-cookie';
import { CONFIG, devOrStg } from '@/config/Config';
import { getAccountId, getUniId } from '@/core/utils/extractCookieId';

export type SelectorField = 'tags' | 'groups' | 'themes' | 'selection';

export interface SelectorParams {
  limit?: number;
  skip?: number;
  themes_limit?: number;
  themes_skip?: number;
}

export interface SelectionItem {
  name: string;
  encoded_ids: string;
}

export interface TagsResponse {
  tags: string[];
}

export interface GroupsResponse {
  groups: string[];
}

export interface ThemesResponse {
  themes: string[];
}

export interface GroupsResponseFromController {
  account_id: number;
  universe_id: number;
  groups: {
    id: string;
    title: string;
  }[];
}

export interface ThemesResponseFromController {
  account_id: number;
  universe_id: number;
  investigation_id: number;
  themes: {
    descriptors: string[];
    title: string;
    name: string;
    id: string;
    langs: string[];
  }[];
  created_at: number;
  updated_at: number;
}

export interface OccupationsResponse {
  occupations: string[];
}

export interface SelectionsResponse {
  selections: SelectionItem[];
}

export interface PatchTagsBody {
  field: 'tagging';
  new_value: string;
}

export interface PatchTagsResponse {
  message?: string;
}

class SelectorService {
  private createAuthenticatedAxios() {
    const axiosInstance = axios.create();

    axiosInstance.interceptors.request.use((config) => {
      const token = Cookies.get(CONFIG.AUTH.SSID);

      if (token) {
        config.headers[CONFIG.AUTH.SSID] = token;
      }

      return config;
    });

    return axiosInstance;
  }

  private async get<T>(serviceName: string, path: string, queryParams?: Record<string, unknown>): Promise<T> {
    const gateway = CONFIG.getGatewayUrl();
    const version = CONFIG.getVersion();
    const url = `${gateway}${serviceName}/${version}${path}`;
    const axiosInstance = this.createAuthenticatedAxios();
    const response = await axiosInstance.get(url, { params: queryParams });
    return response.data;
  }

  private async fetchField(fields: SelectorField, params: SelectorParams): Promise<string[]> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const queryParams: Record<string, unknown> = { from_datastore: true, fields };
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    const data = await this.get<Record<string, unknown>>(
      'panels-service',
      `/v2/panels/${acc_id}/${uni_id}`,
      queryParams,
    );
    const raw = (data?.[fields] ?? (data?.message as Record<string, unknown>)?.[fields] ?? []) as unknown[];
    if (!Array.isArray(raw)) return [];

    // Only split by comma for tags (large string), keep other fields as-is
    if (fields === 'tags') {
      return raw.flatMap((item) => String(item).split(',').map((s) => s.trim())).filter(Boolean);
    }
    return raw.map(String).filter(Boolean);
  }

  async getTags(params: SelectorParams): Promise<TagsResponse> {
    const tags = await this.fetchField('tags', params);
    return { tags };
  }

  async getGroups(params: SelectorParams): Promise<GroupsResponse> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const queryParams: Record<string, unknown> = { from_datastore: true, fields: 'groups' };
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    const data = await this.get<GroupsResponseFromController>(
      'panels-service',
      `/v2/panels/${acc_id}/${uni_id}`,
      queryParams,
    );
    const raw: unknown[] = (data as any)?.groups ?? (data as any)?.message?.groups ?? (data as any)?.names ?? [];
    const groups: string[] = Array.isArray(raw)
      ? raw.map((item) => {
          const value = typeof item === 'string' ? item : (item as { title?: string })?.title ?? '';
          return value;
        }).filter(Boolean)
      : [];
    return { groups };
  }

  async getThemes(params: SelectorParams): Promise<ThemesResponse> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const queryParams: Record<string, unknown> = { from_datastore: true };
    if (params?.themes_skip !== undefined) queryParams.themes_skip = params.themes_skip;
    if (params?.themes_limit !== undefined) queryParams.themes_limit = params.themes_limit;
    const data = await this.get<ThemesResponseFromController>(
      'panels-service',
      `/v2/themes/${acc_id}/${uni_id}`,
      queryParams,
    );
    const raw: unknown[] = (data as any)?.themes ?? (data as any)?.message?.themes ?? (data as any)?.themes ?? [];
    const themes: string[] = Array.isArray(raw)
      ? raw.map((item) => {
          if (typeof item === 'string') {
            return item;
          }
          if (item != null && typeof item === 'object') {
            return (item as { title?: string }).title ?? '';
          }
          return String(item);
        })
      : [];
    return { themes };
  }

  async getThemesFromController(): Promise<ThemesResponse> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const params: Record<string, unknown> = { from_datastore: true, use_cache: true };
    if (!devOrStg()) {
      params.passkey = CONFIG.THEMES_PASSKEY.PRODUCTION;
    }
    const data = await this.get<ThemesResponseFromController>(
      'panels-service',
      `/themes/${acc_id}/${uni_id}`,
      params,
    );
    const themes = data?.themes?.map((theme) => theme.title).filter(Boolean) ?? [];
    return { themes };
  }

  async addTag(tags: string[]): Promise<PatchTagsResponse> {
    return this.addTags(tags);
  }

  async addTags(tags: string[]): Promise<PatchTagsResponse> {
    const gateway = CONFIG.getGatewayUrl();
    const version = CONFIG.getVersion();
    const acc_id = getAccountId();
    const uni_id = getUniId();

    const url = `${gateway}panels-service/${version}/v2/panels/datastore/${uni_id}?account_id=${acc_id}`;

    const axiosInstance = this.createAuthenticatedAxios();

    if (tags.length === 0) return {};

    // The PATCH replaces the whole list; merge with the backend's current tags so
    // tags added elsewhere since this screen loaded are not erased. Strict fetch:
    // any failed page aborts rather than overwriting with a partial list.
    const latestTags = await this.fetchItemsPaginatedParallel('tags', 500, 5, true);
    const mergedTags = Array.from(new Set([...latestTags, ...tags]));

    const tagsBody: PatchTagsBody = {
      field: 'tagging',
      new_value: mergedTags.join(','),
    };
    const response = await axiosInstance.patch(url, tagsBody);
    return response.data;
  }

  async getOccupations(params: SelectorParams): Promise<OccupationsResponse> {
    const account_id = getAccountId();
    const passkey = Cookies.get(CONFIG.AUTH.SSID) ?? '';
    const queryParams: Record<string, unknown> = { account_id, passkey };
    if (params?.skip !== undefined) queryParams.skip = params.skip;
    if (params?.limit !== undefined) queryParams.limit = params.limit;
    const data = await this.get<OccupationsResponse>(
      'stilingue-endpoint',
      '/endpoint/occupations',
      queryParams,
    );
    const raw: unknown[] = (data as any)?.occupations ?? (data as any)?.message?.occupations ?? [];
    const occupations: string[] = Array.isArray(raw)
      ? raw.map(String).filter(Boolean)
      : [];
    return { occupations };
  }

  async getSelections(): Promise<SelectionsResponse> {
    const uni_id = getUniId();
    const data = await this.get<SelectionItem[] | { data: SelectionItem[] }>(
      'panels-service',
      `/v2/post-selections/${uni_id}`,
    );
    
    let list: SelectionItem[] = [];
    if (Array.isArray(data)) {
      list = data;
    } else {
      const dataAsAny = data as any;
      if (Array.isArray(dataAsAny?.data)) {
        list = dataAsAny.data;
      }
    }
    
    const selections = list.filter((item) => item?.name);
    return { selections };
  }

  /**
   * Parse groups from response data, extracting title field from objects.
   */
  private parseGroupsData(data: unknown): string[] {
    const dataAsAny = data as any;
    const raw: unknown[] = dataAsAny?.groups ?? dataAsAny?.message?.groups ?? dataAsAny?.names ?? [];
    return Array.isArray(raw)
      ? raw.map((item) => {
          const value = typeof item === 'string' ? item : (item as { title?: string })?.title ?? '';
          return value;
        }).filter(Boolean)
      : [];
  }

  /**
   * Parse themes from response data, extracting title field from objects or using string values.
   */
  private parseThemesData(data: unknown): string[] {
    const dataAsAny = data as any;
    const raw: unknown[] = dataAsAny?.themes ?? dataAsAny?.message?.themes ?? dataAsAny?.themes ?? [];
    return Array.isArray(raw)
      ? raw.map((item) => {
          if (typeof item === 'string') {
            return item;
          }
          if (item != null && typeof item === 'object') {
            return (item as { title?: string }).title ?? '';
          }
          return String(item);
        })
      : [];
  }

  /**
   * Parse occupations from response data.
   */
  private parseOccupationsData(data: unknown): string[] {
    const dataAsAny = data as any;
    const raw: unknown[] = dataAsAny?.occupations ?? dataAsAny?.message?.occupations ?? [];
    return Array.isArray(raw) ? raw.map(String).filter(Boolean) : [];
  }

  /**
   * Extract fulfilled data from a PromiseSettledResult or return direct data.
   * Returns null only for rejected promises; direct data is returned as-is.
   */
  private extractFulfilledData(result: unknown): unknown {
    if (!result || typeof result !== 'object' || !('status' in result)) {
      return result; // Direct data (backward compatibility)
    }
    const settled = result as PromiseSettledResult<unknown>;
    if (settled.status === 'rejected') {
      return null; // Skip failed requests
    }
    return settled.value;
  }

  /**
   * Process paginated batch results, accumulating items until an empty or incomplete page is found.
   * Handles both direct data and PromiseSettledResult objects, skipping rejections.
   */
  private async processPaginatedBatch(
    allItems: string[],
    batchResults: unknown[],
    parseFunction: (data: unknown) => string[],
    pageSize: number,
  ): Promise<{ foundEmpty: boolean; hadFulfilled: boolean }> {
    let hadFulfilled = false;
    for (const result of batchResults) {
      const data = this.extractFulfilledData(result);
      if (data === null) continue; // Skip rejected promises

      hadFulfilled = true;
      const items = parseFunction(data);
      allItems.push(...items);

      // Stop if we found an empty or incomplete page
      if (items.length === 0 || items.length < pageSize) {
        return { foundEmpty: true, hadFulfilled };
      }
    }
    return { foundEmpty: false, hadFulfilled };
  }

  /**
   * Fetches all items of a given field with optimized parallel pagination.
   * First request checks if additional pages are needed.
   * Only parallelizes if data exceeds pageSize.
   * Uses deduplication to detect backends that ignore skip/limit and return the
   * same data on every page — in that case the loop breaks immediately.
   * @param field The field type to fetch
   * @param pageSize Items per request (default: 500)
   * @param parallelRequests Number of parallel requests per batch (default: 5)
   * @param strict When true, a rejected page request rejects the whole fetch instead of being skipped
   */
  private async fetchItemsPaginatedParallel(
    field: SelectorField,
    pageSize: number = 500,
    parallelRequests: number = 5,
    strict: boolean = false,
  ): Promise<string[]> {
    // First request to check if pagination is needed
    const firstPage = await this.fetchField(field, {
      limit: pageSize,
      skip: 0,
    });

    // If first page is empty or incomplete, no need for more requests
    if (firstPage.length === 0 || firstPage.length < pageSize) {
      return firstPage;
    }

    // Use a Set to deduplicate and detect when the backend returns the same page
    // repeatedly (i.e. it does not honour skip/limit). In that scenario no new
    // items are added and the loop terminates instead of running forever.
    const seen = new Set<string>(firstPage);
    let pageIndex = 1;
    const MAX_ITERATIONS = 20; // safety cap: 20 batches × 5 pages × 500 = 50 000 items max

    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      const promises = Array.from({ length: parallelRequests }, (_, i) =>
        this.fetchField(field, {
          limit: pageSize,
          skip: (pageIndex + i) * pageSize,
        }),
      );

      const batchResults = await Promise.allSettled(promises);
      const { foundTerminal, hadFulfilled } = this.consumeFieldBatch(batchResults, seen, pageSize, strict);

      if (foundTerminal || !hadFulfilled) return Array.from(seen);
      pageIndex += parallelRequests;
    }

    if (strict) {
      throw new Error(`Pagination for "${field}" reached the ${MAX_ITERATIONS}-batch cap without a terminal page`);
    }

    return Array.from(seen);
  }

  /**
   * Adds the items of each fulfilled page to `seen`, stopping at the first terminal page.
   * In strict mode a rejected page throws instead of being skipped.
   */
  private consumeFieldBatch(
    batchResults: PromiseSettledResult<string[]>[],
    seen: Set<string>,
    pageSize: number,
    strict: boolean,
  ): { foundTerminal: boolean; hadFulfilled: boolean } {
    let hadFulfilled = false;

    for (const result of batchResults) {
      if (result.status === 'rejected') {
        if (strict) throw result.reason;
        continue;
      }

      hadFulfilled = true;
      const items = result.value;
      const sizeBefore = seen.size;
      items.forEach((item) => seen.add(item));
      const addedNew = seen.size > sizeBefore;

      // Terminal: empty or incomplete page, or only items we already have
      if (items.length < pageSize || !addedNew) {
        return { foundTerminal: true, hadFulfilled };
      }
    }

    return { foundTerminal: false, hadFulfilled };
  }

  /**
   * Fetches all tags with parallel pagination (500 items per request, 5 parallel requests)
   */
  async getAllTagsPaginated(): Promise<TagsResponse> {
    const tags = await this.fetchItemsPaginatedParallel('tags');
    return { tags };
  }

  /**
   * Fetches all groups with optimized pagination.
   * First request checks if additional pages are needed.
   */
  async getAllGroupsPaginated(): Promise<GroupsResponse> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const pageSize = 500;
    const parallelRequests = 5;

    // First request
    const firstData = await this.get<GroupsResponseFromController>(
      'panels-service',
      `/v2/panels/${acc_id}/${uni_id}`,
      { from_datastore: true, fields: 'groups', skip: 0, limit: pageSize },
    ).catch((): GroupsResponseFromController => ({ account_id: 0, universe_id: 0, groups: [] }));

    const firstGroups = this.parseGroupsData(firstData);

    // If first page is incomplete, no need for more requests
    if (firstGroups.length === 0 || firstGroups.length < pageSize) {
      return { groups: firstGroups };
    }

    const allGroups = [...firstGroups];
    let pageIndex = 1;
    const MAX_ITERATIONS = 20;

    // Parallelize remaining pages
    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      const promises = Array.from({ length: parallelRequests }, (_, i) =>
        this.get<GroupsResponseFromController>(
          'panels-service',
          `/v2/panels/${acc_id}/${uni_id}`,
          { from_datastore: true, fields: 'groups', skip: (pageIndex + i) * pageSize, limit: pageSize },
        ),
      );

      const batchResults = await Promise.allSettled(promises);
      const { foundEmpty, hadFulfilled } = await this.processPaginatedBatch(allGroups, batchResults, (data) => this.parseGroupsData(data), pageSize);

      if (foundEmpty || !hadFulfilled) break;
      pageIndex += parallelRequests;
    }

    return { groups: allGroups };
  }

  /**
   * Fetches all themes with optimized pagination.
   * First request checks if additional pages are needed.
   */
  async getAllThemesPaginated(): Promise<ThemesResponse> {
    const acc_id = getAccountId();
    const uni_id = getUniId();
    const pageSize = 500;
    const parallelRequests = 5;

    // First request
    const firstData = await this.get<ThemesResponseFromController>(
      'panels-service',
      `/v2/themes/${acc_id}/${uni_id}`,
      { from_datastore: true, themes_skip: 0, themes_limit: pageSize },
    ).catch((): ThemesResponseFromController => ({ account_id: 0, universe_id: 0, investigation_id: 0, themes: [], created_at: 0, updated_at: 0 }));

    const firstThemes = this.parseThemesData(firstData);

    // If first page is incomplete, no need for more requests
    if (firstThemes.length === 0 || firstThemes.length < pageSize) {
      return { themes: firstThemes };
    }

    const allThemes = [...firstThemes];
    let pageIndex = 1;
    const MAX_ITERATIONS = 20;

    // Parallelize remaining pages
    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      const promises = Array.from({ length: parallelRequests }, (_, i) =>
        this.get<ThemesResponseFromController>(
          'panels-service',
          `/v2/themes/${acc_id}/${uni_id}`,
          { from_datastore: true, themes_skip: (pageIndex + i) * pageSize, themes_limit: pageSize },
        ),
      );

      const batchResults = await Promise.allSettled(promises);
      const { foundEmpty, hadFulfilled } = await this.processPaginatedBatch(allThemes, batchResults, (data) => this.parseThemesData(data), pageSize);

      if (foundEmpty || !hadFulfilled) break;
      pageIndex += parallelRequests;
    }

    return { themes: allThemes };
  }

  /**
   * Fetches all occupations with optimized pagination.
   * First request checks if additional pages are needed.
   */
  async getAllOccupationsPaginated(): Promise<OccupationsResponse> {
    const account_id = getAccountId();
    const passkey = Cookies.get(CONFIG.AUTH.SSID) ?? '';
    const pageSize = 500;
    const parallelRequests = 5;

    // First request
    const firstData = await this.get<OccupationsResponse>(
      'stilingue-endpoint',
      '/endpoint/occupations',
      { account_id, passkey, skip: 0, limit: pageSize },
    ).catch((): OccupationsResponse => ({ occupations: [] }));

    const firstOccupations = this.parseOccupationsData(firstData);

    // If first page is incomplete, no need for more requests
    if (firstOccupations.length === 0 || firstOccupations.length < pageSize) {
      return { occupations: firstOccupations };
    }

    const allOccupations = [...firstOccupations];
    let pageIndex = 1;
    const MAX_ITERATIONS = 20;

    // Parallelize remaining pages
    for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
      const promises = Array.from({ length: parallelRequests }, (_, i) =>
        this.get<OccupationsResponse>(
          'stilingue-endpoint',
          '/endpoint/occupations',
          { account_id, passkey, skip: (pageIndex + i) * pageSize, limit: pageSize },
        ),
      );

      const batchResults = await Promise.allSettled(promises);
      const { foundEmpty, hadFulfilled } = await this.processPaginatedBatch(allOccupations, batchResults, (data) => this.parseOccupationsData(data), pageSize);

      if (foundEmpty || !hadFulfilled) break;
      pageIndex += parallelRequests;
    }

    return { occupations: allOccupations };
  }

  async searchOccupations(term: string): Promise<string[]> {
    const account_id = getAccountId();
    const passkey = Cookies.get(CONFIG.AUTH.SSID) ?? '';

    const baseUrl = CONFIG.getServiceUrl('stilingue-endpoint');
    const url = `${baseUrl}/endpoint/occupations`;
    const queryParams = { account_id, passkey, q: term };

    const axiosInstance = this.createAuthenticatedAxios();
    const response = await axiosInstance.get<OccupationsResponse>(url, {
      params: queryParams,
    });

    return this.parseOccupationsData(response.data);
  }
}

const selectorService = new SelectorService();
export default selectorService;
